import { getDatadogLogs } from '../clients/datadog-logs';
import { getDatadogRum } from '../clients/datadog-rum';
import { isBotSession } from '../config/is-bot-session';
import { validateConfig } from '../config/validate';
function applyDatadogGlobalContextTags(instance, { platform, env, country, locationId, additionalTags, subService, }) {
    instance.setGlobalContextProperty('platform', platform);
    instance.setGlobalContextProperty('env', env);
    // Optional regional/store tags. Only set when provided so apps that aren't
    // tied to a country or store don't emit a literal `undefined` tag value to Datadog.
    if (country !== undefined) {
        instance.setGlobalContextProperty('country', country);
    }
    if (locationId !== undefined) {
        instance.setGlobalContextProperty('location_id', locationId);
    }
    if (subService !== undefined) {
        instance.setGlobalContextProperty('sub_service', subService);
    }
    // Safe to apply after the reserved tags: validateConfig already rejects any
    // additionalTags that reuse reserved keys, so callers cannot overwrite them.
    if (additionalTags) {
        const tags = Object.entries(additionalTags);
        for (const [key, value] of tags) {
            instance.setGlobalContextProperty(key, value);
        }
    }
}
/**
 * The context tree is stored on `globalThis` so that all separately-bundled copies
 * of this module (e.g. shopify-storefront vs custom-made vs measurements-client
 * each bundling their own copy of @susu/datadog) share a single source of truth.
 * Module-level variables would be isolated per bundle instance and break the
 * parent→child relationship across app boundaries.
 */
const TREE_KEY = '__susuDDContextTree';
function getTree() {
    const global = globalThis;
    if (!global[TREE_KEY]) {
        global[TREE_KEY] = { registry: new Map(), leaf: null };
    }
    return global[TREE_KEY];
}
function findNearestActiveAncestor(node) {
    let current = node.parent;
    while (current !== null && !current.isActive) {
        current = current.parent;
    }
    return current;
}
// Initialize the Datadog RUM instance
export async function initDatadogRum(config) {
    validateConfig(config);
    const { service, env, version, applicationId, clientToken, site = 'datadoghq.eu', sessionSampleRate, sessionReplaySampleRate, trackUserInteractions = true, trackResources = true, trackLongTasks = true, defaultPrivacyLevel = 'mask-user-input', storeContextsAcrossPages, useSecureSessionCookie, platform, subService, country, locationId, additionalTags, rumBeforeSend, } = config;
    const rum = await getDatadogRum();
    if (!rum.getInitConfiguration()) {
        const isBot = isBotSession();
        rum.init({
            applicationId,
            clientToken,
            site,
            service,
            env,
            version,
            sessionSampleRate: isBot ? 0 : sessionSampleRate,
            sessionReplaySampleRate: isBot ? 0 : sessionReplaySampleRate,
            trackUserInteractions,
            trackResources,
            trackLongTasks,
            defaultPrivacyLevel,
            storeContextsAcrossPages,
            useSecureSessionCookie,
            beforeSend: rumBeforeSend,
        });
    }
    // Always-present reserved tags. `platform` and `env` are required by config
    // so they're safe to write unconditionally.
    applyDatadogGlobalContextTags(rum, {
        platform,
        env,
        country,
        locationId,
        additionalTags,
        subService,
    });
    return rum;
}
// Initialize the Datadog Logs instance
export async function initDatadogLogs(config) {
    validateConfig(config);
    const { service, env, version, clientToken, site = 'datadoghq.eu', logsSampleRate, forwardErrorsToLogs = true, storeContextsAcrossPages, useSecureSessionCookie, subService, platform, country, locationId, additionalTags, logsBeforeSend, } = config;
    const logs = await getDatadogLogs();
    if (!logs.getInitConfiguration()) {
        logs.init({
            clientToken,
            site,
            service,
            env,
            version,
            sessionSampleRate: isBotSession() ? 0 : logsSampleRate,
            forwardErrorsToLogs,
            storeContextsAcrossPages,
            useSecureSessionCookie,
            beforeSend: logsBeforeSend,
        });
    }
    // Always-present reserved tags. `platform` and `env` are required by config
    // so they're safe to write unconditionally.
    applyDatadogGlobalContextTags(logs, {
        platform,
        env,
        country,
        locationId,
        additionalTags,
        subService,
    });
    return logs;
}
/**
 * Initialize the Datadog RUM and Logs instances.
 *
 * Handles two scenarios transparently:
 *
 * **Standalone** — RUM has not been initialized yet. Both SDK instances are
 * initialized with the provided config and global context tags are applied.
 *
 * **Embedded** — A host application has already initialized the RUM singleton.
 * `rum.init()` / `logs.init()` are skipped, but global context tags are
 * overwritten so events from the embedded app carry the correct context.
 *
 * In both cases the returned `cleanup` hands global context back to whoever
 * owned it before this call. Cleanup calls may arrive out of order (e.g. a
 * parent unmounts before its child): the ownership tree handles this by
 * reparenting children to the nearest active ancestor when a middle node cleans
 * up, so the child's eventual cleanup always restores the correct context.
 */
export async function initDatadog(config) {
    // Snapshot global context BEFORE applying this frame's tags so that
    // restore() can replay this frame from a clean baseline later.
    const [preRum, preLogs] = await Promise.all([
        getDatadogRum(),
        getDatadogLogs(),
    ]);
    const rumPreSnapshot = { ...preRum.getGlobalContext() };
    const logsPreSnapshot = { ...preLogs.getGlobalContext() };
    const [rum, logs] = await Promise.all([
        initDatadogRum(config),
        initDatadogLogs(config),
    ]);
    if (!config.subService) {
        rum.setGlobalContextProperty('domain', config.service);
        logs.setGlobalContextProperty('domain', config.service);
    }
    const id = Symbol();
    /**
     * Replays this node's context from its pre-init baseline.
     * Restoring the snapshot first clears any keys a deeper node wrote that
     * this node does not set, so no stale tags bleed through.
     */
    const restore = () => {
        rum.setGlobalContext({ ...rumPreSnapshot });
        logs.setGlobalContext({ ...logsPreSnapshot });
        applyDatadogGlobalContextTags(rum, config);
        applyDatadogGlobalContextTags(logs, config);
        if (!config.subService) {
            rum.setGlobalContextProperty('domain', config.service);
            logs.setGlobalContextProperty('domain', config.service);
        }
    };
    const tree = getTree();
    const node = { id, parent: tree.leaf, isActive: true, restore };
    tree.registry.set(id, node);
    tree.leaf = node;
    const cleanup = () => {
        if (!node.isActive)
            return;
        // Always read from the shared tree so cross-bundle updates are visible.
        const t = getTree();
        node.isActive = false;
        t.registry.delete(id);
        const effectiveParent = findNearestActiveAncestor(node);
        // Reparent direct children to the nearest still-active ancestor so that
        // their cleanup calls will restore the right context even if this node
        // (their original parent) already cleaned up out of order.
        for (const n of t.registry.values()) {
            if (n.parent === node) {
                n.parent = effectiveParent;
            }
        }
        // If this node was the leaf (context owner), hand off to the effective parent.
        if (t.leaf === node) {
            t.leaf = effectiveParent;
            if (effectiveParent) {
                effectiveParent.restore();
            }
            else {
                // Root/standalone cleanup: no parent in the tree. Restore to the
                // pre-init snapshot so all context this frame set is cleared.
                rum.setGlobalContext({ ...rumPreSnapshot });
                logs.setGlobalContext({ ...logsPreSnapshot });
            }
        }
        // If not the leaf, a deeper active node controls context. It was just
        // reparented above, so its own cleanup will find the right ancestor.
    };
    return { rum, logs, cleanup };
}
